import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Plus, Trash2, Users, Car, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Input } from "../ui/input";

const rid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
const fieldCls = "bg-[#0C121E] border-slate-800/80 h-10 text-sm";

export const DRIVER_FIELDS = [{ key: "name", label: "Nome e cognome" }, { key: "phone", label: "Telefono" }];
export const VEHICLE_FIELDS = [{ key: "model", label: "Modello" }, { key: "plate", label: "Targa" }];

function RowsEditor({ title, icon: Icon, rows, setRows, fields, testid, addLabel }) {
  const update = (id, key, v) => setRows((list) => list.map((r) => (r.id === id ? { ...r, [key]: v } : r)));
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-amber-300 font-bold inline-flex items-center gap-1.5"><Icon size={12} />{title} ({rows.filter((r) => (r[fields[0].key] || "").trim()).length})</div>
      {rows.length === 0 && <div className="text-xs text-slate-500 italic">Nessun elemento</div>}
      {rows.map((r, i) => (
        <div key={r.id} className="flex flex-col sm:flex-row gap-2 rounded-lg border border-slate-800 bg-slate-900/30 p-2" data-testid={`${testid}-row-${i}`}>
          {fields.map((f) => (
            <Input key={f.key} placeholder={f.label} value={r[f.key] || ""} onChange={(e) => update(r.id, f.key, f.key === "plate" ? e.target.value.toUpperCase() : e.target.value)}
              className={fieldCls} data-testid={`${testid}-row-${i}-${f.key}`} />
          ))}
          <button type="button" onClick={() => setRows((list) => list.filter((x) => x.id !== r.id))} title="Rimuovi"
            className="shrink-0 self-end sm:self-auto h-10 w-10 inline-flex items-center justify-center rounded-lg border border-slate-800 text-slate-500 hover:text-rose-400 hover:border-rose-500/50" data-testid={`${testid}-row-${i}-remove`}><Trash2 size={14} /></button>
        </div>
      ))}
      <button type="button" onClick={() => setRows((list) => [...list, { id: rid(), ...Object.fromEntries(fields.map((f) => [f.key, ""])) }])}
        className="inline-flex items-center gap-1.5 rounded-lg px-3 h-9 text-xs font-semibold border border-amber-500/50 text-amber-200 hover:bg-amber-500/10" data-testid={`${testid}-add`}>
        <Plus size={14} /> {addLabel}
      </button>
    </div>
  );
}

// Editor autisti/veicoli esterni: righe modificabili, salvataggio su PUT /suppliers/{id}/fleet e rilettura dal DB.
export function SupplierFleetEditor({ supplier, onSaved, onDone }) {
  const [drivers, setDrivers] = useState(supplier.drivers || []);
  const [vehicles, setVehicles] = useState(supplier.vehicles || []);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    api.get(`/suppliers/${supplier.id}`).then((r) => {
      if (!alive) return;
      setDrivers(r.data.drivers || []); setVehicles(r.data.vehicles || []);
    }).catch((e) => toast.error(formatApiError(e))).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [supplier.id]);

  const save = async () => {
    const bad = drivers.find((d) => !(d.name || "").trim() && (d.phone || "").trim()) || vehicles.find((v) => !(v.model || "").trim() && (v.plate || "").trim());
    if (bad) { toast.error("Compila nome dell'autista / modello del veicolo in ogni riga"); return; }
    setBusy(true);
    try {
      await api.put(`/suppliers/${supplier.id}/fleet`, { drivers, vehicles });
      const fresh = (await api.get(`/suppliers/${supplier.id}`)).data;
      const want = (rows, k) => rows.filter((r) => (r[k] || "").trim()).length;
      if ((fresh.drivers || []).length !== want(drivers, "name") || (fresh.vehicles || []).length !== want(vehicles, "model")) {
        throw new Error("I dati salvati non corrispondono: riprova");
      }
      setDrivers(fresh.drivers || []); setVehicles(fresh.vehicles || []);
      onSaved?.(fresh);
      window.dispatchEvent(new CustomEvent("supplier-fleet-saved", { detail: fresh }));
      toast.success(`Salvati ${fresh.drivers.length} autisti e ${fresh.vehicles.length} veicoli`);
      onDone?.();
    } catch (e) { toast.error(e?.response ? formatApiError(e) : e.message); }
    setBusy(false);
  };

  if (loading) return <div className="text-xs text-slate-500 inline-flex items-center gap-2 py-4"><Loader2 size={14} className="animate-spin" />Caricamento...</div>;
  return (
    <div className="space-y-5" data-testid="supplier-fleet-editor">
      <RowsEditor title="Autisti esterni" icon={Users} rows={drivers} setRows={setDrivers} fields={DRIVER_FIELDS} testid="supplier-drivers" addLabel="Aggiungi autista" />
      <RowsEditor title="Veicoli esterni" icon={Car} rows={vehicles} setRows={setVehicles} fields={VEHICLE_FIELDS} testid="supplier-vehicles" addLabel="Aggiungi veicolo" />
      <p className="text-[11px] text-slate-500">Solo anagrafica del fornitore: gli autisti esterni non accedono all'app e non entrano nei conteggi degli autisti interni.</p>
      <div className="flex justify-end">
        <button type="button" disabled={busy} onClick={save} data-testid="supplier-fleet-save"
          className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold bg-gradient-to-b from-[#F1D27A] to-[#C99A24] text-black disabled:opacity-60">
          {busy && <Loader2 size={14} className="animate-spin" />}Salva autisti e veicoli
        </button>
      </div>
    </div>
  );
}

function FleetModal({ supplier, onClose, onSaved }) {
  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="supplier-fleet-modal">
      <div className="w-full max-w-2xl bg-[#0A0907] border border-amber-500/40 rounded-t-2xl sm:rounded-2xl max-h-[92dvh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-amber-500/20">
          <div><div className="font-display text-lg text-white">Autisti e veicoli</div><div className="text-xs text-slate-400">{supplier.name}</div></div>
          <button type="button" onClick={onClose} className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-slate-700 text-slate-300" data-testid="supplier-fleet-close"><X size={16} /></button>
        </div>
        <div className="p-5 overflow-y-auto" style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}>
          <SupplierFleetEditor supplier={supplier} onSaved={onSaved} onDone={onClose} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function SupplierFleetCell({ supplier }) {
  const [s, setS] = useState(supplier);
  const [open, setOpen] = useState(false);
  useEffect(() => { setS(supplier); }, [supplier]);
  useEffect(() => {
    const h = (e) => { if (e.detail?.id === supplier.id) setS(e.detail); };
    window.addEventListener("supplier-fleet-saved", h);
    return () => window.removeEventListener("supplier-fleet-saved", h);
  }, [supplier.id]);
  return (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }} data-testid={`supplier-fleet-btn-${s.id}`}
        className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs border border-amber-500/40 text-amber-200 hover:bg-amber-500/10 whitespace-nowrap">
        <Users size={12} /><span data-testid={`supplier-fleet-drivers-count-${s.id}`}>{(s.drivers || []).length}</span> · <Car size={12} /><span data-testid={`supplier-fleet-vehicles-count-${s.id}`}>{(s.vehicles || []).length}</span>
      </button>
      {open && <FleetModal supplier={s} onClose={() => setOpen(false)} onSaved={setS} />}
    </>
  );
}
